import type { AssignedCouponsAPIResponse } from '@app/pages/api/assigned-coupons/types';
import {
  ADD_GIFT_CARD_PUBLIC_ENDPOINT,
  ASSIGNED_COUPONS_PUBLIC_ENDPOINT,
  CART_ITEMS_PUBLIC_ENDPOINT,
  CART_MONOLITH_PUBLIC_ENDPOINT,
  COUPON_MONOLITH_PUBLIC_ENDPOINT,
  DELIVERY_DATES_PUBLIC_ENDPOINT,
  SHIPPING_PREFERENCE_PUBLIC_ENDPOINT,
  USER_SETTINGS_PUBLIC_ENDPOINT,
} from '@app/pages/api/constants';
import type {
  DeliveryDatesAPIResponse,
  DeliveryDatesApiResponse,
} from '@app/pages/api/delivery-dates/types';
import { DeliveryTimeFrameType } from '@app/pages/api/delivery-dates/types';
import {
  AddLineItemAPIRequest,
  AddLineItemAPIResponse,
  AddLineItemAPISuccess,
  ApplyCouponAPIResponse,
  CartQueryShippingAddress,
  RemoveCouponAPIResponse,
  UpdateLineItemAPIResponse,
  UpdateShippingDetailsAPIRequest,
} from '@app/types/monolith/types';
import logger from '@redbubble/next/logger';
import { getHost } from '@redbubble/utils/getHost';
import { addBusinessDays, format as formatDate } from 'date-fns';
import { Cart, ShippingPreference } from 'types/monolith/cart';
import {
  UpdateUserSettingsRequestBody,
  UserSettings,
} from 'types/monolith/user-settings';
import { ProcessedAssignedCoupons } from '../monolithClients/assigned-coupons/types';
import { AddGiftCardRequestBody } from '../monolithClients/gift-cards/types';
import { DeliveryDatesFetchState } from './types';
import { shippingAddressToCartParams } from './utils';

export class ResponseError extends Error {
  status: number;

  statusText: string;

  responseBody: string;

  constructor(response: Response, bodyError?: string) {
    super(
      bodyError
        ? `ResponseError: ${response.status} ${response.statusText}: ${bodyError}`
        : `ResponseError: ${response.status} ${response.statusText}`
    );
    this.status = response.status;
    this.statusText = response.statusText;
    this.responseBody = bodyError ?? 'no response body';
  }
}

type UpdateUserSettingsProps = UpdateUserSettingsRequestBody;

/**
 * @param {Date} [dateFrom=new Date()] - Starting point from which to generate the delivery dates from.
 * If no dateFrom parameter is given, defaults to the current time.
 */
export const getFallbackDeliveryDates = (
  dateFrom: Date = new Date()
): DeliveryDatesApiResponse => {
  const dateFormat = 'yyyy-MM-dd';
  const fallbackStandardEarliestBusinessDay = 15;
  const fallbackStandardLatestBusinessDay = 17;
  const fallbackExpressBusinessDay = 6;

  return {
    standard: {
      type: DeliveryTimeFrameType.DeliveryTimeFrame,
      earliest: formatDate(
        addBusinessDays(dateFrom, fallbackStandardEarliestBusinessDay),
        dateFormat
      ),
      latest: formatDate(
        addBusinessDays(dateFrom, fallbackStandardLatestBusinessDay),
        dateFormat
      ),
    },

    express: {
      type: DeliveryTimeFrameType.DeliveryTimeFrame,
      earliest: formatDate(
        addBusinessDays(dateFrom, fallbackExpressBusinessDay),
        dateFormat
      ),
      latest: formatDate(
        addBusinessDays(dateFrom, fallbackExpressBusinessDay),
        dateFormat
      ),
    },
  };
};

export type LoggingInfo = {
  metricName: string;
  context: string;
};

export type UpdateCartLineItemRequest = {
  id: string;
  quantity: number;
};

export type ApplyCouponRequest = {
  couponCode: string;
  shippingAddress?: CartQueryShippingAddress | null;
};

export type RemoveCouponRequest = {
  shippingAddress?: CartQueryShippingAddress | null;
};

export class CartCheckoutClient {
  logging: LoggingInfo;

  constructor({ logging }: { logging: LoggingInfo }) {
    this.logging = logging;
  }

  async fetchDeliveryDates(
    {
      countryCode,
      productTypeIds,
      fetchState,
    }: {
      countryCode: string;
      productTypeIds: string[];
      fetchState?: DeliveryDatesFetchState;
    },
    signal?: AbortSignal
  ): Promise<DeliveryDatesApiResponse> {
    if (!productTypeIds.length) {
      return getFallbackDeliveryDates();
    }

    const queryParams = {
      country_code: countryCode.toLowerCase(),
      product_type_ids: productTypeIds.join(','),
      timezone_offset: formatDate(new Date(), 'xxx'),
    };

    const url = new URL(DELIVERY_DATES_PUBLIC_ENDPOINT, getHost());
    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then(async (res) => {
        const jsonData: DeliveryDatesAPIResponse = await res.json();
        if (jsonData && 'error' in jsonData) {
          throw new ResponseError(res, jsonData.error);
        }
        if (!res.ok) {
          throw new ResponseError(res);
        }
        return jsonData;
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to fetch delivery dates: ${error.message}`;
        logger.error(
          {
            err: error,
            queryParams,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::${fetchState}`;
        logger.info(
          {
            err: { step: fetchState },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        return getFallbackDeliveryDates();
      });
  }

  async fetchCartAndUpdateShippingCountry(
    signal?: AbortSignal,
    shippingAddress?: CartQueryShippingAddress
  ): Promise<Cart> {
    const url = new URL(CART_MONOLITH_PUBLIC_ENDPOINT, getHost());
    const addressParams = shippingAddressToCartParams(shippingAddress);

    return fetch(url, {
      method: 'PATCH',
      ...(addressParams && { body: JSON.stringify(addressParams) }),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new ResponseError(res);
        }
        return res.json();
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to fetch cart: ${error.message}`;
        logger.error(
          {
            err: error,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::cart-refetch`;
        logger.info(
          {
            err: { step: 'cart-refetch' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async updateShippingDetails(
    params: UpdateShippingDetailsAPIRequest,
    signal?: AbortSignal
  ): Promise<Cart> {
    const url = new URL(CART_MONOLITH_PUBLIC_ENDPOINT, getHost());
    const addressParams = shippingAddressToCartParams(params);

    return fetch(url, {
      method: 'PATCH',
      body: JSON.stringify(addressParams),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new ResponseError(res);
        }
        return res.json();
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to update shipping details: ${error.message}`;
        logger.error(
          {
            err: error,
            requestBody: { addressParams },
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::cart-refetch`;
        logger.info(
          {
            err: { step: 'cart-refetch' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async updateShippingPreference(
    preference: ShippingPreference,
    signal?: AbortSignal
  ): Promise<{ cart: Cart }> {
    const url = new URL(SHIPPING_PREFERENCE_PUBLIC_ENDPOINT, getHost());
    return fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preference }),
      ...(signal && { signal }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new ResponseError(res);
        }

        return res.json();
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to update shipping preference: ${error.message}`;
        logger.error(
          {
            err: error,
            context: 'CartAndDeliveryDatesDataContext/apiClient',
            message,
            requestBody: { preference },
          },
          message
        );

        throw error;
      });
  }

  async updateUserSettings(
    requestBody: UpdateUserSettingsProps,
    signal?: AbortSignal
  ): Promise<UserSettings> {
    const step = 'country-update';

    const url = new URL(USER_SETTINGS_PUBLIC_ENDPOINT, getHost());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      ...(signal && { signal }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new ResponseError(response);
        }
        return response.json();
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to update user settings: ${error.message}`;
        logger.error(
          {
            err: error,
            context: this.logging.context,
            message,
            requestBody,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::${step}`;
        logger.info(
          {
            err: { step },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async addCartLineItem(
    requestBody: AddLineItemAPIRequest,
    signal?: AbortSignal
  ): Promise<AddLineItemAPISuccess> {
    const url = new URL(CART_ITEMS_PUBLIC_ENDPOINT, getHost());

    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if ([200, 400, 404, 422].includes(res.status)) return res.json();

        throw new ResponseError(res);
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to add line item to cart: ${error.message}`;
        logger.error(
          {
            err: error,
            requestBody,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::cart-item-add`;
        logger.info(
          {
            err: { step: 'cart-item-add' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async updateCartLineItem(
    requestBody: UpdateCartLineItemRequest,
    signal?: AbortSignal
  ): Promise<UpdateLineItemAPIResponse> {
    const url = new URL(CART_MONOLITH_PUBLIC_ENDPOINT, getHost());

    return fetch(url, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if ([200, 422].includes(res.status)) return res.json();

        throw new ResponseError(res);
      })
      .catch((error) => {
        // we don't want to log the abort controller errors
        if (error.name === 'AbortError') {
          throw error;
        }

        const message = `CartAndDeliveryDatesDataContext failed to update cart line item quantity: ${error.message}`;
        logger.error(
          {
            err: error,
            requestBody,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::cart-quantity-update`;
        logger.info(
          {
            err: { step: 'cart-quantity-update' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async applyCoupon(
    params: ApplyCouponRequest,
    signal?: AbortSignal
  ): Promise<ApplyCouponAPIResponse> {
    const url = new URL(COUPON_MONOLITH_PUBLIC_ENDPOINT, getHost());
    const addressParams = shippingAddressToCartParams(params.shippingAddress);
    const body = {
      coupon_code: params.couponCode,
      ...addressParams,
    };

    return fetch(url, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if ([200, 400, 401, 404].includes(res.status)) return res.json();

        throw new ResponseError(res);
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to apply a coupon: ${error.message}`;
        logger.error(
          {
            err: error,
            body,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::apply-coupon`;
        logger.info(
          {
            err: { step: 'apply-coupon' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async removeCoupon(
    params: RemoveCouponRequest,
    signal?: AbortSignal
  ): Promise<RemoveCouponAPIResponse> {
    const url = new URL(COUPON_MONOLITH_PUBLIC_ENDPOINT, getHost());
    const addressParams = shippingAddressToCartParams(params.shippingAddress);

    return fetch(url, {
      method: 'DELETE',
      ...(addressParams && { body: JSON.stringify(addressParams) }),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if ([200, 404].includes(res.status)) return res.json();

        throw new ResponseError(res);
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to remove a coupon: ${error.message}`;
        logger.error(
          {
            err: error,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::remove-coupon`;
        logger.info(
          {
            err: { step: 'remove-coupon' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async fetchAssignedCoupons(
    signal?: AbortSignal
  ): Promise<ProcessedAssignedCoupons> {
    const url = new URL(ASSIGNED_COUPONS_PUBLIC_ENDPOINT, getHost());

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then(async (res) => {
        if (!res.ok) throw new ResponseError(res);

        const jsonData: AssignedCouponsAPIResponse = await res.json();

        if ('error' in jsonData) throw new Error(jsonData.error);

        return jsonData;
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to fetch assigned coupons: ${error.message}`;
        logger.error(
          {
            err: error,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::assigned-coupons`;
        logger.info(
          {
            err: { step: 'assigned-coupons' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }

  async addGiftCard(
    requestBody: AddGiftCardRequestBody,
    signal?: AbortSignal
  ): Promise<AddLineItemAPIResponse> {
    const url = new URL(ADD_GIFT_CARD_PUBLIC_ENDPOINT, getHost());

    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
      ...(signal && { signal }),
    })
      .then((res) => {
        if ([200, 201, 400, 404, 422].includes(res.status)) return res.json();

        throw new ResponseError(res);
      })
      .catch((error) => {
        const message = `CartAndDeliveryDatesDataContext failed to add gift card to cart: ${error.message}`;
        logger.error(
          {
            err: error,
            requestBody,
            context: this.logging.context,
            message,
          },
          message
        );

        const infoMessage = `${this.logging.metricName}::cart-item-add-gift-card`;
        logger.info(
          {
            err: { step: 'cart-item-add' },
            context: this.logging.context,
            message: infoMessage,
          },
          infoMessage
        );

        throw error;
      });
  }
}
